import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  Tooltip,
  Legend
);

const ProductDistributionComponent = ({ nav }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState("all");
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const dealerLogin = useSelector((state) => state.dealerLogin);
  const { dealerInfo } = dealerLogin;

  const fetchData = async () => {
    if (dealerInfo && dealerInfo._id) {
      try {
        const params = { dealerId: dealerInfo._id, timePeriod };
        if (timePeriod === "monthly") {
          params.month = month;
          params.year = year;
        } else if (timePeriod === "yearly") {
          params.year = year;
        }
        const response = await axios.get(
          `${process.env.REACT_APP_DP_API}/api/dealer-portal/product-distribution`,
          {
            params,
            headers: { Authorization: `Bearer ${dealerInfo.token}` },
          }
        );
        setData(response.data);
        setLoading(false);
      } catch (err) {
        setError(
          err.response?.data?.message || "Error fetching product distribution"
        );
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [dealerInfo, timePeriod, month, year]);

  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value);
    setLoading(true);
  };

  const handleMonthChange = (event) => {
    setMonth(event.target.value);
    setLoading(true);
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
    setLoading(true);
  };

  const years = Array.from({ length: 5 }, (_, i) =>
    (new Date().getFullYear() - i).toString()
  );
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString());

  if (loading) {
    return (
      <div className="container p-4">
        <div className="d-flex flex-wrap mb-4 gap-4">
          <Skeleton
            variant="rectangular"
            width={200}
            height={40}
            className="rounded"
          />
          <Skeleton
            variant="rectangular"
            width={200}
            height={40}
            className="rounded"
          />
          <Skeleton
            variant="rectangular"
            width={200}
            height={40}
            className="rounded"
          />
        </div>
        <div className="bg-white rounded p-4 shadow">
          <Skeleton variant="text" width="80%" height={20} className="mb-3" />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            className="rounded mb-3"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            className="rounded mb-3"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={100}
            className="rounded"
          />
        </div>
      </div>
    );
  }

  if (error) return <div className="text-danger text-center">{error}</div>;
  if (
    !data ||
    !data.productDistribution ||
    data.productDistribution.length === 0
  ) {
    return (
      <div className="text-muted text-center">
        No Product Distribution Data Available
      </div>
    );
  }

  const pieChartDataOrders = {
    labels: data.productDistribution.map((item) => item.productType),
    datasets: [
      {
        data: data.productDistribution.map((item) => item.count),
        backgroundColor: [
          "#007bff",
          "#28a745",
          "#dc3545",
          "#fd7e14",
          "#0d6efd",
        ],
      },
    ],
  };

  const pieChartDataRevenue = {
    labels: data.productDistribution.map((item) => item.productType),
    datasets: [
      {
        data: data.productDistribution.map((item) => item.revenue),
        backgroundColor: [
          "#007bff",
          "#28a745",
          "#dc3545",
          "#fd7e14",
          "#0d6efd",
        ],
      },
    ],
  };

  const barChartData = {
    labels: data.productDistribution.map((item) => item.productType),
    datasets: [
      {
        label: "Processing",
        data: data.productDistribution.map(
          (item) => item.statusBreakdown.processing
        ),
        backgroundColor: "#007bff",
      },
      {
        label: "Processed",
        data: data.productDistribution.map(
          (item) => item.statusBreakdown.processed
        ),
        backgroundColor: "#28a745",
      },
      {
        label: "Delivered",
        data: data.productDistribution.map(
          (item) => item.statusBreakdown.delivered
        ),
        backgroundColor: "#dc3545",
      },
      {
        label: "On Hold",
        data: data.productDistribution.map(
          (item) => item.statusBreakdown.onHold
        ),
        backgroundColor: "#fd7e14",
      },
    ],
  };

  return (
    <div className="container p-4">
      <div className="d-flex flex-wrap mb-4 gap-4">
        <FormControl className="w-48">
          <InputLabel>Time Period</InputLabel>
          <Select
            value={timePeriod}
            onChange={handleTimePeriodChange}
            label="Time Period"
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>
        {timePeriod === "monthly" && (
          <>
            <FormControl className="w-48">
              <InputLabel>Month</InputLabel>
              <Select value={month} onChange={handleMonthChange} label="Month">
                {months.map((m) => (
                  <MenuItem key={m} value={m}>
                    {new Date(0, m - 1).toLocaleString("default", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className="w-48">
              <InputLabel>Year</InputLabel>
              <Select value={year} onChange={handleYearChange} label="Year">
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {timePeriod === "yearly" && (
          <FormControl className="w-48">
            <InputLabel>Year</InputLabel>
            <Select value={year} onChange={handleYearChange} label="Year">
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
      <h2 className="h2 fw-bold mb-4">
        Product Distribution - {data.periodName}
      </h2>
      <div className="row row-cols-1 row-cols-md-2 g-4 mb-4">
        <div className="col">
          <div className="bg-white rounded p-4 shadow">
            <h3 className="h3 fw-bold mb-3">Order Distribution</h3>
            <div style={{ height: "16rem" }}>
              <Pie
                data={pieChartDataOrders}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "top" } },
                }}
              />
            </div>
          </div>
        </div>
        <div className="col">
          <div className="bg-white rounded p-4 shadow">
            <h3 className="h3 fw-bold mb-3">Revenue Distribution</h3>
            <div style={{ height: "16rem" }}>
              <Pie
                data={pieChartDataRevenue}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "top" } },
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded p-4 shadow">
        <h3 className="h3 fw-bold mb-3">
          Product Status Breakdown (Total Orders: {data.totalOrders}, Total
          Revenue: ${data.totalRevenue.toLocaleString()})
        </h3>
        {data.topProduct && (
          <p className="text-muted mb-3">Top Product: {data.topProduct}</p>
        )}
        <div style={{ height: "16rem" }}>
          <Bar
            data={barChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { position: "top" } },
              scales: { x: { stacked: true }, y: { stacked: true } },
            }}
          />
        </div>
        <table className="table table-bordered mt-3">
          <thead>
            <tr className="table-light">
              <th scope="col" className="text-left">Product Type</th>
              <th scope="col" className="text-left">Orders</th>
              <th scope="col" className="text-left">Revenue</th>
              <th scope="col" className="text-left">Processing</th>
              <th scope="col" className="text-left">Processed</th>
              <th scope="col" className="text-left">Delivered</th>
              <th scope="col" className="text-left">On Hold</th>
            </tr>
          </thead>
          <tbody>
            {data.productDistribution.map((item, index) => (
              <tr key={index}>
                <td>{item.productType}</td>
                <td>
                  {item.count} ({item.countPercentage}%)
                </td>
                <td>
                  ${item.revenue.toLocaleString()} ({item.revenuePercentage}%)
                </td>
                <td>{item.statusBreakdown.processing}</td>
                <td>{item.statusBreakdown.processed}</td>
                <td>{item.statusBreakdown.delivered}</td>
                <td>{item.statusBreakdown.onHold}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductDistributionComponent;